/**
 * Contenuti del documento di offerta, compilati insieme all'offerta.
 *
 * Stanno qui e non in una finestra separata: standard, attività e fasi sono
 * dati dell'offerta, non del momento in cui si stampa il Word. Vengono salvati
 * con l'offerta e riproposti a ogni generazione.
 */
import { useEffect, useState } from 'react';
import { documentoApi, templateApi } from '../api';
import { giornatePreventivo } from './calcoloPreventivo';

const label = 'block text-xs font-medium text-gray-500 mb-0.5';

// Le attività si modificano come testo, una per riga: più immediato di un
// editor a righe quando si vuole ritoccare l'elenco in blocco.
export const aTesto = (righe) => (righe || []).join('\n');
export const daTesto = (t) => String(t || '').split('\n').map(x => x.trim()).filter(Boolean);


/**
 * Settimane ricavate dalla durata scritta a parole ("circa 2 mesi",
 * "da 3 a 4 mesi"). Il template dichiara la durata in italiano; senza questa
 * lettura ogni fase nasceva di 4 settimane a prescindere.
 * Quando ci sono due numeri si prende il maggiore: meglio una previsione
 * prudente che un cronoprogramma che sfora al primo imprevisto.
 */
export function settimaneDaDurata(testo) {
  const t = String(testo || '').toLowerCase();
  // Si leggono SOLO i numeri attaccati all'unità: "circa 1 mese, comprensivo
  // di Stage 1 e Stage 2" dura un mese, non due — gli altri numeri nella frase
  // non sono durate.
  const cerca = (unita) => {
    const m = t.match(new RegExp(`(\\d+)(?:\\s*(?:a|-|–|e)\\s*(\\d+))?\\s*${unita}`));
    if (!m) return null;
    return Math.max(Number(m[1]), Number(m[2] || m[1]));
  };
  const mesi = cerca('mes');
  if (mesi) return Math.max(1, Math.round(mesi * 4.33));
  const sett = cerca('settiman');
  if (sett) return sett;
  const anni = cerca('ann');
  if (anni) return anni * 52;
  return 4;
}

/**
 * Termini di pagamento usati finora in tutte le offerte. Restano il valore di
 * partenza, ma da qui in poi sono un campo come gli altri: si cambiano offerta
 * per offerta (30 giorni, fine mese, RIBA…) senza toccare il codice.
 * Le offerte scritte prima non hanno il campo: per loro vale questa frase,
 * esattamente come prima.
 */
export const TERMINI_PREDEFINITI =
  'Il pagamento dovrà essere effettuato a mezzo Bonifico Bancario a 60 gg data fattura.';

export const docVuoto = () => ({
  standard: 'iso9001', taglio: 'assistenza',
  tipologia_id: '', template_id: '',
  destinatario: '', saluto: '', premessa: '',
  attivitaTesto: '', fasi: [],
  km: '0,65', pagamento: 'SAL 100% a fine attività',
  pagamento_termini: TERMINI_PREDEFINITI,
  data_inizio_prevista: '', mostraGantt: true,
});

const gg = (n) => String(Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(1)).replace('.', ',');

// Quanto vale una unità in giornate: serve a non sommare ore e giornate come
// se fossero la stessa cosa. Un listino a ore dava "62 giornate" invece di
// "62 ore" (offerta FB3126).
const GIORNATE_PER_UNITA = { giorno: 1, mezza_giornata: 0.5, ora: 1 / 8 };

/**
 * L'impegno previsto dal listino, con l'unità in cui è stato scritto.
 *
 * Se le voci usano tutte la stessa unità si mostra quella — "62 ore" dice al
 * cliente quello che ha davanti; se sono miste si converte in giornate, che è
 * l'unico modo di sommarle.
 */
export function impegnoListino(voci = []) {
  const conQta = voci.filter(v => Number(v.quantita_prevista) > 0);
  const giornate = conQta.reduce(
    (s, v) => s + Number(v.quantita_prevista) * (GIORNATE_PER_UNITA[v.unita] ?? 0), 0);
  const unita = [...new Set(conQta.map(v => v.unita))];
  const omogenea = unita.length === 1 ? unita[0] : null;
  const quantita = omogenea
    ? conQta.reduce((s, v) => s + Number(v.quantita_prevista), 0) : giornate;
  return { quantita, unita: omogenea || 'giorno', giornate };
}

const NOME_UNITA = {
  giorno: ['giornata', 'giornate'], mezza_giornata: ['mezza giornata', 'mezze giornate'],
  ora: ['ora', 'ore'], intervento: ['attività', 'attività'],
};
export const etichettaImpegno = ({ quantita, unita }) => {
  const nomi = NOME_UNITA[unita] || NOME_UNITA.giorno;
  return `${gg(quantita)} ${quantita === 1 ? nomi[0] : nomi[1]}`;
};

/** Totale GIORNATE: dalle fasi se ci sono, altrimenti convertito dal listino. */
export function totaleGiornate(doc, voci) {
  const daFasi = (doc?.fasi || []).reduce((s, f) => s + (Number(f.giornate) || 0), 0);
  if (daFasi > 0) return daFasi;
  return impegnoListino(voci).giornate;
}

export default function PannelloDocumento({ doc, onChange, giornateListino = 0, impegno = null,
                                            parte = 'tutto' }) {
  // La scheda dell'offerta mette fra i contenuti e la pianificazione il
  // prezzo e le tariffe aggiuntive: il pannello si apre in due, e ogni metà
  // si rende dove serve invece di costringere a scorrere avanti e indietro.
  const mostraContenuti = parte === 'tutto' || parte === 'contenuti';
  const mostraPianificazione = parte === 'tutto' || parte === 'pianificazione';
  const [modelli, setModelli] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [nuovaTipologia, setNuovaTipologia] = useState(null);   // nome in digitazione
  const set = (k, v) => onChange({ ...doc, [k]: v });

  const caricaTipologie = () => templateApi.tipologie()
    .then(r => setTipologie(r.filter(x => x.attivo)))
    .catch(() => setTipologie([]));

  useEffect(() => {
    // Modelli e tipologie vengono entrambi dai template salvati: così uno
    // standard aggiunto dal gestionale compare nella tendina e, scegliendo
    // tipologia e template, i suoi contenuti si applicano da soli.
    documentoApi.modelli().then(setModelli).catch(() => {});
    caricaTipologie();
  }, []);

  const tipologiaScelta = tipologie.find(t => String(t.id) === String(doc.tipologia_id));
  const templateDisponibili = tipologiaScelta?.template || [];

  // Scegliendo la tipologia si applica il suo template predefinito, senza
  // sovrascrivere i testi già scritti a mano.
  const scegliTipologia = (id) => {
    const t = tipologie.find(x => String(x.id) === String(id));
    const pre = t?.template?.find(x => x.predefinito) || t?.template?.[0];
    const base = { ...doc, tipologia_id: id || '', template_id: pre?.template_id || '' };
    // Una sola scrittura: tipologia, template e contenuti insieme.
    onChange(pre?.chiave && modelli.some(m => m.chiave === pre.chiave)
      ? contenutiModello(base, pre.chiave, doc.taglio, false)
      : base);
  };

  const scegliTemplate = (templateId) => {
    const scelto = templateDisponibili.find(x => String(x.template_id) === String(templateId));
    const base = { ...doc, template_id: templateId || '' };
    // forza=true: scegliere esplicitamente un altro template deve sostituire
    // i contenuti proposti, altrimenti la scelta non si vede.
    onChange(scelto?.chiave && modelli.some(m => m.chiave === scelto.chiave)
      ? contenutiModello(base, scelto.chiave, doc.taglio, true)
      : base);
  };

  const creaTipologia = async () => {
    const nome = String(nuovaTipologia || '').trim();
    if (!nome) return setNuovaTipologia(null);
    try {
      // Nasce già abbinata allo standard selezionato: senza template la
      // tipologia non saprebbe cosa proporre alla prossima offerta.
      const tpl = await templateApi.list({ attivi: 1 });
      const corrente = tpl.find(x => x.chiave === doc.standard);
      const creata = await templateApi.creaTipologia({
        nome, template: corrente ? [{ template_id: corrente.id, predefinito: true }] : [],
      });
      await caricaTipologie();
      onChange({ ...doc, tipologia_id: creata.id, template_id: corrente?.id || '' });
      setNuovaTipologia(null);
    } catch (e) { alert(e); }
  };

  // Cambiando standard o taglio si ricaricano i contenuti proposti.
  // Non si sovrascrive quello che è già stato scritto a mano.
  // Restituisce i campi da applicare, SENZA scrivere: chi chiama li fonde nel
  // proprio aggiornamento. Scrivere qui dentro partendo da `doc` significava
  // usare una copia vecchia e cancellare la tipologia appena scelta.
  const contenutiModello = (base, standard, taglio, forza) => {
    const m = modelli.find(x => x.chiave === standard);
    if (!m) return { ...base, standard, taglio };
    const anno = new Date().getFullYear();
    const nuovo = { ...base, standard, taglio };
    if (forza || !base.attivitaTesto) {
      nuovo.oggettoProposto = (m.oggetto[taglio] || '').replace('{ANNO}', anno);
      nuovo.attivitaTesto = taglio === 'assistenza' ? aTesto(m.attivita) : '';
    }
    if (forza || !(base.fasi || []).length) {
      // Ogni fase parte dove finisce la precedente. Il vecchio passo fisso di
      // 4 settimane faceva sovrapporre le fasi appena una durava di più.
      let prossima = 1;
      nuovo.fasi = taglio === 'implementazione'
        ? m.fasi.map((x) => {
            const settimane = settimaneDaDurata(x.durata);
            const fase = {
              nome: x.nome, durata: x.durata, inizio: prossima, settimane,
              giornate: '', attivitaTesto: aTesto(x.attivita),
            };
            prossima += settimane;
            return fase;
          })
        : [];
    }
    return nuovo;
  };

  const applicaModello = (standard, taglio, forza) =>
    onChange(contenutiModello(doc, standard, taglio, forza));

  const setFase = (i, k, v) => set('fasi', doc.fasi.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const togliFase = (i) => set('fasi', doc.fasi.filter((_, j) => j !== i));

  const ggFasi = (doc.fasi || []).reduce((s, f) => s + (Number(f.giornate) || 0), 0);
  const ggTot = ggFasi > 0 ? ggFasi : giornateListino;

  return (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-3">
      <div>
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {mostraContenuti ? 'Contenuti del documento' : 'Cronoprogramma e condizioni'}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {mostraContenuti
            ? "Servono a generare l'offerta in Word/PDF. Si salvano con l'offerta: potrai "
              + 'rigenerare il documento in qualsiasi momento senza reinserirli.'
            : "Tempi dell'intervento e condizioni economiche: finiscono in fondo al documento."}
        </p>
      </div>

      {mostraContenuti && (<>
      {/* Tipologie in tabella: due tendine da aprire e confrontare non dicevano
          quale template ciascuna porta con sé, e la scelta si faceva alla
          cieca. Qui si vede tutto insieme e si sceglie con un clic. */}
      <div>
        <div className="flex items-end justify-between mb-1">
          <label className={label}>Tipologia di offerta</label>
          {nuovaTipologia === null && (
            <button type="button" className="btn-secondary btn-sm"
                    onClick={() => setNuovaTipologia('')}>+ Tipologia</button>
          )}
        </div>

        {nuovaTipologia !== null && (
          <div className="flex gap-1 mb-2">
            <input className="input text-sm" autoFocus placeholder="es. Sicurezza"
                   value={nuovaTipologia} onChange={e => setNuovaTipologia(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); creaTipologia(); }
                                     if (e.key === 'Escape') setNuovaTipologia(null); }} />
            <button type="button" className="btn-primary btn-sm shrink-0" onClick={creaTipologia}>✓</button>
            <button type="button" className="btn-secondary btn-sm shrink-0" onClick={() => setNuovaTipologia(null)}>✕</button>
          </div>
        )}

        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                <th className="px-3 py-1.5 w-8"></th>
                <th className="px-3 py-1.5">Tipologia</th>
                <th className="px-3 py-1.5">Template</th>
              </tr>
            </thead>
            <tbody>
              <tr className={`border-b cursor-pointer hover:bg-gray-50 ${!doc.tipologia_id ? 'bg-brand-50/70' : ''}`}
                  onClick={() => scegliTipologia('')}>
                <td className="px-3 py-1.5">
                  <input type="radio" readOnly checked={!doc.tipologia_id} className="w-4 h-4" />
                </td>
                <td className="px-3 py-1.5 text-gray-500">— nessuna —</td>
                <td className="px-3 py-1.5 text-xs text-gray-400">contenuti scritti a mano</td>
              </tr>
              {tipologie.map(t => {
                const scelta = String(t.id) === String(doc.tipologia_id);
                const suoi = t.template || [];
                return (
                  <tr key={t.id}
                      className={`border-b last:border-0 cursor-pointer hover:bg-gray-50 ${scelta ? 'bg-brand-50/70' : ''}`}
                      onClick={() => scegliTipologia(t.id)}>
                    <td className="px-3 py-1.5">
                      <input type="radio" readOnly checked={scelta} className="w-4 h-4" />
                    </td>
                    <td className="px-3 py-1.5 font-medium text-gray-800">{t.nome}</td>
                    <td className="px-3 py-1.5">
                      {/* Con un solo template non c'è niente da scegliere: si
                          scrive quale sarà. Con più di uno si sceglie qui. */}
                      {!suoi.length ? (
                        <span className="text-xs text-amber-700">nessun template abbinato</span>
                      ) : !scelta || suoi.length === 1 ? (
                        <span className="text-xs text-gray-500">
                          {(suoi.find(x => x.predefinito) || suoi[0]).nome}
                          {suoi.length > 1 && ` e altri ${suoi.length - 1}`}
                        </span>
                      ) : (
                        <select className="input py-0.5 text-xs" value={doc.template_id || ''}
                                onClick={e => e.stopPropagation()}
                                onChange={e => scegliTemplate(e.target.value)}>
                          <option value="">— scegli —</option>
                          {suoi.map(x => (
                            <option key={x.template_id} value={x.template_id}>
                              {x.nome}{x.predefinito ? ' (predefinito)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Standard / schema</label>
          <select className="input text-sm" value={doc.standard || ''}
                  onChange={e => applicaModello(e.target.value, doc.taglio, true)}>
            {/* Lo standard salvato con l'offerta resta visibile anche se il
                template nel frattempo è stato disattivato: altrimenti la
                tendina appare vuota e il primo salvataggio lo cambia. */}
            {doc.standard && !modelli.some(m => m.chiave === doc.standard) && (
              <option value={doc.standard}>{doc.standard} (non più in elenco)</option>
            )}
            {modelli.map(m => <option key={m.chiave} value={m.chiave}>{m.nome}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Tipo di intervento</label>
          <select className="input text-sm" value={doc.taglio}
                  onChange={e => applicaModello(doc.standard, e.target.value, true)}>
            <option value="assistenza">Assistenza / mantenimento annuale</option>
            <option value="implementazione">Implementazione fino a certificazione</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Alla c.a. di</label>
          <input className="input text-sm" placeholder="es. Dott. Mario Rossi — Ufficio Qualità"
                 value={doc.destinatario || ''} onChange={e => set('destinatario', e.target.value)} />
        </div>
        <div>
          <label className={label}>Saluto iniziale</label>
          <input className="input text-sm" placeholder="es. Gent.mo Dott. Rossi,"
                 value={doc.saluto || ''} onChange={e => set('saluto', e.target.value)} />
        </div>
      </div>

      {doc.taglio === 'assistenza' && (
        <div>
          <label className={label}>Attività prevista <span className="font-normal">(una per riga)</span></label>
          <textarea className="input font-mono text-xs" rows={6} value={doc.attivitaTesto || ''}
                    onChange={e => set('attivitaTesto', e.target.value)} />
        </div>
      )}

      {/* Cosa esporre al cliente. Tempi e costi sono due scelte separate:
          a volte si manda l'impegno senza i prezzi (il corrispettivo sta in un
          accordo quadro o in una lettera a parte), a volte il prezzo senza il
          conto delle giornate. */}
      {(() => {
        const tempi = doc.mostraTempi !== false;
        const costi = doc.mostraCosti !== false;
        // I tempi devono arrivare da qualche parte: le quantità a listino,
        // oppure il dettaglio del preventivo. Senza né l'uno né l'altro,
        // "solo tempi" produrrebbe un documento senza alcun tempo.
        const haImpegno = !!(impegno && impegno.quantita > 0)
          || !!(doc.preventivo?.attivo && doc.preventivo?.mostraDettaglio);
        return (
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Cosa esporre al cliente
            </span>
            <div className="flex gap-5">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="w-4 h-4" checked={tempi}
                       onChange={e => set('mostraTempi', e.target.checked)} />
                Tempi <span className="text-xs text-gray-500">(giornate e ore)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="w-4 h-4" checked={costi}
                       onChange={e => set('mostraCosti', e.target.checked)} />
                Dettaglio costi <span className="text-xs text-gray-500">(composizione e tariffe)</span>
              </label>
            </div>
            {/* L'importo totale c'è sempre: queste spunte tolgono il dettaglio,
                non il prezzo. */}
            <p className="text-xs text-gray-500">
              {tempi && costi && 'Documento completo: impegno, composizione del prezzo e totale.'}
              {tempi && !costi && (haImpegno
                ? 'Esce l’impegno previsto e il totale, senza la composizione del prezzo '
                  + 'né le tariffe unitarie.'
                : 'Esce il totale senza la sua composizione. Quest’offerta non ha un impegno da '
                  + 'mostrare: servirebbero le quantità a listino o il dettaglio del preventivo.')}
              {!tempi && costi && 'Esce la composizione del prezzo senza le quantità, e il '
                + 'cronoprogramma senza le giornate.'}
              {!tempi && !costi && 'Esce il solo totale: niente giornate, niente composizione '
                + 'del prezzo, niente tariffe.'}
            </p>
          </div>
        );
      })()}

      {/* Dettaglio dell'impegno: la tabella "attività + ore" che va al cliente.
          Su certe offerte è esattamente ciò che il cliente vuole vedere, su
          altre è un invito a discutere ogni singola ora: si sceglie. */}
      {doc.mostraTempi !== false && impegno && impegno.quantita > 0 && (
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="w-4 h-4 mt-0.5" checked={doc.mostraImpegno !== false}
                 onChange={e => set('mostraImpegno', e.target.checked)} />
          <span>
            Mostra al cliente il dettaglio dell'impegno (attività e {NOME_UNITA[impegno.unita]?.[1] || 'quantità'})
            <span className="block text-xs text-gray-500">
              {doc.mostraImpegno !== false
                ? `Nel documento esce la tabella delle attività con l'impegno massimo (${etichettaImpegno(impegno)} in tutto). Le tariffe unitarie non compaiono.`
                : 'Nel documento resta il solo importo massimo, senza il dettaglio per attività.'}
            </span>
          </span>
        </label>
      )}

      </>)}

      {mostraPianificazione && (<>
      {/* Cronoprogramma: nasce dalle fasi. Prima le fasi comparivano solo per
          le implementazioni e non si capiva perché il Gantt a volte usciva e a
          volte no: ora è una scelta esplicita, per qualsiasi intervento. */}
      {(() => {
        const fasi = doc.fasi || [];
        const includi = doc.mostraGantt !== false && (fasi.length > 0 || doc.taglio === 'implementazione');
        return (
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="w-4 h-4 mt-0.5" checked={includi}
                   onChange={e => onChange(e.target.checked
                     ? { ...doc, mostraGantt: true,
                         fasi: fasi.length ? fasi : [{ nome: 'Fase 1', durata: '', inizio: 1, settimane: 2,
                                                       giornate: '', attivitaTesto: '' }] }
                     : { ...doc, mostraGantt: false })} />
            <span>
              Includi il cronoprogramma nel documento
              <span className="block text-xs text-gray-500">
                {!includi ? 'Il documento uscirà senza Gantt.'
                  : fasi.length
                    ? `Uscirà un Gantt con ${fasi.length} ${fasi.length === 1 ? 'fase' : 'fasi'}, a partire dall'avvio previsto.`
                    : 'Aggiungi almeno una fase: il Gantt si costruisce dalle fasi.'}
              </span>
            </span>
          </label>
        );
      })()}

      {(doc.taglio === 'implementazione' || (doc.mostraGantt !== false && (doc.fasi || []).length > 0)) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Fasi del progetto</span>
            {/* Con un preventivo, le giornate si distribuiscono fra le fasi:
                si vede subito quante ne restano da assegnare. */}
            {doc.preventivo?.attivo && giornatePreventivo(doc.preventivo) > 0 ? (() => {
              const previste = giornatePreventivo(doc.preventivo);
              const diff = Math.round((previste - ggFasi) * 100) / 100;
              return (
                <span className="text-xs text-gray-600">
                  Preventivo <strong>{gg(previste)} gg</strong> · nelle fasi{' '}
                  <strong className={diff === 0 ? 'text-green-700' : diff < 0 ? 'text-red-600' : 'text-amber-700'}>
                    {gg(ggFasi)} gg
                  </strong>
                  {diff > 0 && <span className="text-amber-700"> · da assegnare {gg(diff)}</span>}
                  {diff < 0 && <span className="text-red-600"> · {gg(-diff)} in più del preventivo</span>}
                  {diff === 0 && <span className="text-green-700"> ✓</span>}
                </span>
              );
            })() : ggFasi > 0 && (
              <span className="text-xs text-gray-600">
                Totale: <strong className="text-brand-700">{gg(ggFasi)} giornate</strong>
              </span>
            )}
          </div>
          {(doc.fasi || []).map((x, i) => (
            <div key={i} className="bg-white border rounded-lg p-2.5 space-y-2">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <label className={label}>Nome fase</label>
                  <input className="input text-sm" value={x.nome || ''} onChange={e => setFase(i, 'nome', e.target.value)} />
                </div>
                <button type="button" onClick={() => togliFase(i)}
                        className="text-gray-400 hover:text-red-600 text-lg leading-none mt-5 px-1"
                        title="Elimina fase">✕</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className={label}>Inizio (settimana)</label>
                  <input className="input text-sm" type="number" min="1" value={x.inizio ?? 1}
                         onChange={e => setFase(i, 'inizio', e.target.value)} />
                </div>
                <div>
                  <label className={label}>Durata (settimane)</label>
                  <input className="input text-sm" type="number" min="1" value={x.settimane ?? 1}
                         onChange={e => setFase(i, 'settimane', e.target.value)} />
                </div>
                <div>
                  <label className={label}>Giornate previste</label>
                  <input className="input text-sm text-right" type="number" step="0.5" min="0"
                         placeholder="gg" value={x.giornate ?? ''}
                         onChange={e => setFase(i, 'giornate', e.target.value)} />
                </div>
                <div>
                  <label className={label}>Durata (testo)</label>
                  <input className="input text-sm" placeholder="es. 2-3 mesi" value={x.durata || ''}
                         onChange={e => setFase(i, 'durata', e.target.value)} />
                </div>
              </div>
              <div>
                <label className={label}>Attività della fase (una per riga)</label>
                <textarea className="input font-mono text-xs" rows={3} value={x.attivitaTesto || ''}
                          onChange={e => setFase(i, 'attivitaTesto', e.target.value)} />
              </div>
              {/* Una fase senza attività e senza durata nel documento sarebbe
                  solo un titolo seguito dal nulla: non esce, e va detto qui
                  invece di farlo scoprire a documento stampato. */}
              {!String(x.attivitaTesto || '').trim() && !String(x.durata || '').trim() && (
                <p className="text-xs text-amber-700">
                  Fase vuota: non comparirà nel documento né nel cronoprogramma.
                  Scrivi le attività (o almeno la durata) per includerla.
                </p>
              )}
            </div>
          ))}
          <div className="flex gap-2 flex-wrap items-center">
            <button type="button" className="btn-secondary btn-sm"
                    onClick={() => {
                      const f = doc.fasi || [];
                      const ultima = f[f.length - 1];
                      const dopo = ultima ? Number(ultima.inizio || 1) + Number(ultima.settimane || 1) : 1;
                      set('fasi', [...f, { nome: `Fase ${f.length + 1}`, durata: '',
                                           inizio: dopo, settimane: 2, giornate: '', attivitaTesto: '' }]);
                    }}>
              + Aggiungi fase
            </button>
            {/* Le sovrapposizioni a volte sono volute, quindi il riordino è un
                comando esplicito e non un automatismo che scatta da solo. */}
            <button type="button" className="btn-secondary btn-sm"
                    title="Fa ripartire ogni fase dove finisce la precedente"
                    onClick={() => {
                      let prossima = 1;
                      set('fasi', (doc.fasi || []).map(x => {
                        const settimane = Math.max(1, Math.trunc(Number(x.settimane)) || 1);
                        const y = { ...x, inizio: prossima, settimane };
                        prossima += settimane;
                        return y;
                      }));
                    }}>
              ↳ Metti in sequenza
            </button>
            {(() => {
              // Avviso quando una fase parte prima che finisca quella prima
              const f = (doc.fasi || []).map(x => ({
                da: Math.trunc(Number(x.inizio)) || 1,
                a: (Math.trunc(Number(x.inizio)) || 1) + (Math.trunc(Number(x.settimane)) || 1) - 1,
              }));
              const sovrapposte = f.some((x, i) => i > 0 && x.da <= f[i - 1].a);
              return sovrapposte ? (
                <span className="text-xs text-amber-700">
                  Alcune fasi si sovrappongono: se non è voluto, usa “Metti in sequenza”.
                </span>
              ) : null;
            })()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className={label}>Avvio previsto (per il Gantt)</label>
          <input className="input text-sm" type="date" value={doc.data_inizio_prevista || ''}
                 onChange={e => set('data_inizio_prevista', e.target.value)} />
        </div>
        <div>
          <label className={label}>Rimborso €/km</label>
          <input className="input text-sm" value={doc.km || ''} onChange={e => set('km', e.target.value)} />
        </div>
        <div>
          <label className={label}>
            Periodicità di fatturazione
            <span className="ml-1 font-normal text-gray-400">(passa alla commessa)</span>
          </label>
          <select className="input text-sm" value={doc.periodicita_mesi ?? ''}
                  onChange={e => set('periodicita_mesi', e.target.value)}>
            <option value="">Non periodica (a SAL / milestone)</option>
            <option value="1">Mensile</option>
            <option value="2">Bimestrale</option>
            <option value="3">Trimestrale</option>
            <option value="4">Quadrimestrale</option>
            <option value="6">Semestrale</option>
          </select>
        </div>
      </div>

      {/* Pagamento: una finestra sola. Erano due campi affiancati e sembravano
          due cose diverse, mentre nel documento sono una sezione unica —
          quando si fattura e quando si incassa. */}
      <div className="bg-white border rounded-lg p-3 space-y-2">
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Pagamento</div>
        <div>
          <label className={label}>Come si fattura</label>
          <input className="input text-sm" list="modalita-pagamento"
                 placeholder="SAL 100% a fine attività"
                 value={doc.pagamento || ''} onChange={e => set('pagamento', e.target.value)} />
          {/* Suggerimenti dalle modalità già in uso, ma il campo resta libero */}
          <datalist id="modalita-pagamento">
            <option value="SAL 100% a fine attività" />
            <option value="50% acconto; 50% saldo post audit terza parte" />
            <option value="Fatturazione a conclusione" />
            <option value="Fatturazione mensile" />
            <option value="Fatturazione bimestrale" />
            <option value="Fatturazione trimestrale" />
            <option value="100% superamento audit terza parte" />
          </datalist>
        </div>
        <div>
          <label className={label}>Quando si paga</label>
          <input className="input text-sm" list="termini-pagamento"
                 placeholder={TERMINI_PREDEFINITI}
                 value={doc.pagamento_termini ?? ''} onChange={e => set('pagamento_termini', e.target.value)} />
          <datalist id="termini-pagamento">
            <option value={TERMINI_PREDEFINITI} />
            <option value="Il pagamento dovrà essere effettuato a mezzo Bonifico Bancario a 30 gg data fattura." />
            <option value="Il pagamento dovrà essere effettuato a mezzo Bonifico Bancario a 60 gg data fattura fine mese." />
            <option value="Il pagamento dovrà essere effettuato a mezzo Bonifico Bancario a 90 gg data fattura." />
            <option value="Il pagamento dovrà essere effettuato a mezzo Bonifico Bancario a vista fattura." />
          </datalist>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Vuoto: nel documento esce la frase di sempre (bonifico a 60 gg data fattura).
          </p>
        </div>
      </div>

      </>)}

      {ggTot > 0 && mostraPianificazione && (
        <div className="border-t pt-2 text-sm text-gray-600">
          Impegno complessivo stimato:{' '}
          <strong className="text-gray-900">
            {ggFasi === 0 && impegno && impegno.quantita > 0
              ? etichettaImpegno(impegno)
              : `${gg(ggTot)} giornate`}
          </strong>
          {ggFasi === 0 && giornateListino > 0 && (
            <span className="text-xs text-gray-400">
              {' '}— dalle quantità del listino
              {impegno && impegno.unita === 'ora' && impegno.giornate > 0
                && ` (${gg(impegno.giornate)} giornate equivalenti)`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
